import React, {useEffect, useRef, useState} from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popper from "@mui/material/Popper";
import {useCalcContext} from "../../contexts/CalcContext";
import Checkbox from "@mui/material/Checkbox";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Stack from "@mui/material/Stack";
import classnames from "classnames";
import {toast} from "react-toastify";
import {loadGoogleMapScript} from "react-google-autocomplete/lib/utils";
import {GOOGLE_MAP_SCRIPT_BASE_URL} from "react-google-autocomplete/lib/constants";
import {buildLocationString, parseGoogleAddressData} from "../../utils/utils";
import {LocationModal} from "../DealPage/LocationModal";
import {useModalsStore} from "../../stores/modalsStore";

// Same Google query as the Edit modal's ZIP field (CreateLocationInput).
const PREDICTION_OPTIONS = {types: ['postal_code'], componentRestrictions: {country: 'us'}}
const SUGGESTION_DEBOUNCE_MS = 300
const GOOGLE_LOAD_TIMEOUT_MS = 10000
const ZIP_PATTERN = /^\d{5}$/
const VIEWPORT_BOUNDED = ['flip', 'preventOverflow'].map((name) => ({
    name, options: {boundary: document.documentElement},
}))

const SUGGESTION_STATUS_TEXT = {
    loading: 'Searching…',
    empty: 'No matching US ZIP codes. Try a 5-digit ZIP.',
    error: 'Location suggestions are unavailable right now.',
}

// react-google-autocomplete's loader: it reuses a Maps script already on the
// page (the Edit modal's, the Listings map's), so the script is added once.
let googleScriptPromise = null
const loadPlacesLibrary = async () => {
    googleScriptPromise ||= Promise.race([
        loadGoogleMapScript(
            GOOGLE_MAP_SCRIPT_BASE_URL,
            `${GOOGLE_MAP_SCRIPT_BASE_URL}?key=${process.env.REACT_APP_GOOGLE_API_KEY}&libraries=places`,
        ),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Google Maps timed out')), GOOGLE_LOAD_TIMEOUT_MS)),
    ]).catch((error) => {
        googleScriptPromise = null // the next keystroke retries
        throw error
    })
    await googleScriptPromise
    const places = window.google?.maps?.places || await window.google?.maps?.importLibrary?.('places')
    if (!places?.AutocompleteService) throw new Error('Google Places is unavailable')
    return places
}

// Google calls back with (result, status); newer builds also return a promise
// that rejects on the same errors, which the callback already reports.
const callGoogle = (request) => new Promise((resolve) => {
    try {
        request((result, status) => resolve({result, status}))?.catch?.(() => {})
    } catch {
        resolve({result: null, status: 'ERROR'})
    }
})

// Bold the part Google matched (the typed digits), like its own dropdown.
const highlightMatches = (text, matches = []) => {
    const parts = []
    let cursor = 0
    for (const {offset, length} of matches) {
        if (offset < cursor) continue
        parts.push(text.slice(cursor, offset))
        parts.push(<span key={offset} className={'location-suggestion-matched'}>{text.slice(offset, offset + length)}</span>)
        cursor = offset + length
    }
    parts.push(text.slice(cursor))
    return parts
}

// Customer / Dealer location as a text field with Google ZIP suggestions.
// Keystrokes stay local: only a picked suggestion, or Enter on a 5-digit ZIP
// Google recognises, reaches `onCommit`.
const LocationZipField = ({id, label, location, onCommit}) => {
    const displayText = buildLocationString(location)
    const [text, setText] = useState(displayText)
    const [isDirty, setIsDirty] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    // Predictions with the text they answer, so Enter can reuse them.
    const [results, setResults] = useState({query: null, predictions: []})
    const suggestions = results.predictions
    const [status, setStatus] = useState('loading')
    const [activeIndex, setActiveIndex] = useState(-1)
    const anchorRef = useRef(null)
    const debounceRef = useRef(null)
    // Bumped by every keystroke, cancel and commit: a response for an older
    // id is dropped, so a slow reply never replaces a newer one.
    const requestIdRef = useRef(0)
    const sessionTokenRef = useRef(null)
    const latestRef = useRef({})
    latestRef.current = {displayText, onCommit}
    const listId = `${id}-suggestions`

    // A pick here or elsewhere (Customer moving Dealer, Reset) replaces the
    // text, unless the user is mid-edit.
    useEffect(() => {
        if (!isDirty) setText(buildLocationString(location))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    useEffect(() => () => {
        clearTimeout(debounceRef.current)
        requestIdRef.current += 1
    }, [])

    const cancelPending = () => {
        clearTimeout(debounceRef.current)
        return ++requestIdRef.current
    }

    // Reopening never shows an earlier search's suggestions.
    const close = () => {
        setIsOpen(false)
        setActiveIndex(-1)
        setResults({query: null, predictions: []})
    }

    const revert = () => {
        cancelPending()
        close()
        setIsDirty(false)
        setText(displayText)
        // An abandoned session just ends; the next search starts a new one.
        sessionTokenRef.current = null
    }

    const fetchPredictions = async (input) => {
        const places = await loadPlacesLibrary()
        sessionTokenRef.current ||= new places.AutocompleteSessionToken()
        const {result, status} = await callGoogle((done) => new places.AutocompleteService().getPlacePredictions(
            {...PREDICTION_OPTIONS, input, sessionToken: sessionTokenRef.current}, done))
        if (status === 'ZERO_RESULTS') return []
        if (status !== 'OK') throw new Error(`Google autocomplete: ${status}`)
        return result || []
    }

    const fetchLocation = async (placeId) => {
        const places = await loadPlacesLibrary()
        const sessionToken = sessionTokenRef.current
        // The details request closes the session it belongs to.
        sessionTokenRef.current = null
        const {result, status} = await callGoogle((done) => new places.PlacesService(document.createElement('div')).getDetails(
            {placeId, fields: ['address_components'], ...(sessionToken && {sessionToken})}, done))
        if (status !== 'OK') throw new Error(`Google place details: ${status}`)
        return parseGoogleAddressData(result?.address_components)
    }

    const onChange = (e) => {
        const value = e.target.value
        setText(value)
        setIsDirty(true)
        setActiveIndex(-1)
        const requestId = cancelPending()
        const query = value.trim()
        if (!query) {
            close()
            return
        }
        setIsOpen(true)
        setStatus('loading')
        debounceRef.current = setTimeout(() => {
            fetchPredictions(query).then((predictions) => {
                if (requestId !== requestIdRef.current) return
                setResults({query, predictions})
                setStatus(predictions.length ? 'ready' : 'empty')
            }).catch(() => {
                if (requestId !== requestIdRef.current) return
                setResults({query: null, predictions: []})
                setStatus('error')
            })
        }, SUGGESTION_DEBOUNCE_MS)
    }

    // `resolve` returns the parsed Google location, or null when Google has
    // none. Typing again cancels it; blurring does not.
    const commit = async (resolve, pendingText, notFoundMessage) => {
        const commitId = cancelPending()
        close()
        setIsDirty(false)
        setText(pendingText)
        let parsed = null
        let failureMessage = notFoundMessage
        try {
            parsed = await resolve()
        } catch {
            failureMessage = "Couldn't reach Google to look up that location. Please try again."
        }
        if (commitId !== requestIdRef.current) return
        if (parsed?.zipcode) {
            setText(buildLocationString(parsed))
            latestRef.current.onCommit(parsed)
        } else {
            setText(latestRef.current.displayText)
            toast.error(failureMessage)
        }
    }

    const selectSuggestion = (prediction) => commit(
        () => fetchLocation(prediction.place_id),
        prediction.description,
        "Google returned no ZIP for that location.",
    )

    const commitTypedZip = (zipcode) => commit(
        async () => {
            const predictions = results.query === zipcode ? results.predictions : await fetchPredictions(zipcode)
            const match = predictions.find((p) => new RegExp(`\\b${zipcode}\\b`).test(p.description || ''))
            return match ? fetchLocation(match.place_id) : null
        },
        zipcode,
        `No US location found for ZIP ${zipcode}.`,
    )

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (!suggestions.length) return
            e.preventDefault()
            const step = e.key === 'ArrowDown' ? 1 : -1
            setActiveIndex((i) => i < 0
                ? (step > 0 ? 0 : suggestions.length - 1)
                : (i + step + suggestions.length) % suggestions.length)
            return
        }
        if (e.key === 'Escape' && isDirty) {
            e.preventDefault()
            e.stopPropagation()
            revert()
            return
        }
        if (e.key !== 'Enter') return
        // Never Desk from here: a pick may still be resolving, so the quote
        // would price at the old location.
        e.preventDefault()
        e.stopPropagation()
        if (!isDirty) return
        const active = suggestions[activeIndex]
        const typed = text.trim()
        if (active) selectSuggestion(active)
        else if (ZIP_PATTERN.test(typed)) commitTypedZip(typed)
    }

    return (
        <>
            <div ref={anchorRef} className={'location-item location-input'}>
                <input
                    className={'location-input-field'}
                    type="text"
                    role="combobox"
                    aria-label={label}
                    aria-autocomplete="list"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={'Enter ZIP code'}
                    value={text}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    // Select the shown location so a typed ZIP replaces it.
                    onFocus={(e) => e.target.select()}
                    // Clicking outside: text left without a pick is never applied.
                    onBlur={() => isDirty && revert()}
                />
            </div>
            {/* Fixed and bounded by the viewport, so the card's overflow: hidden
                neither clips nor flips it. */}
            <Popper
                open={isOpen}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                disablePortal
                popperOptions={{strategy: 'fixed'}}
                modifiers={VIEWPORT_BOUNDED}
                className={'location-suggestions'}
                style={{width: anchorRef.current?.offsetWidth}}
            >
                {suggestions.length > 0
                    ? (
                        <ul id={listId} role="listbox" aria-label={`${label} suggestions`}>
                            {suggestions.map((prediction, index) => (
                                <li
                                    key={prediction.place_id}
                                    id={`${listId}-${index}`}
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={classnames('location-suggestion', {active: index === activeIndex})}
                                    // Keep focus in the field so blur doesn't revert first.
                                    onMouseDown={(e) => e.preventDefault()}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => selectSuggestion(prediction)}
                                >
                                    <span className={'location-suggestion-icon'} aria-hidden="true"/>
                                    <span className={'location-suggestion-main'}>
                                        {highlightMatches(
                                            prediction.structured_formatting?.main_text || prediction.description,
                                            prediction.structured_formatting?.main_text_matched_substrings,
                                        )}
                                    </span>
                                    <span className={'location-suggestion-secondary'}>
                                        {prediction.structured_formatting?.secondary_text}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )
                    : <div id={listId} className={'location-suggestions-status'} role="status">{SUGGESTION_STATUS_TEXT[status]}</div>
                }
                {/* Google's own "powered by Google" logo, as on the Edit modal's dropdown. */}
                <div className={'location-suggestions-attribution'} role="img" aria-label="Powered by Google"/>
            </Popper>
        </>
    )
}

export const CalcPageSelectLocation = () => {
    const {
        calcState,
        saveLocation,
    } = useCalcContext()
    const openModal = useModalsStore((state) => state.openModal)

    // Whether a Customer pick moves the Dealer too, here and as the modal's
    // starting value. A page-level toggle: never saved, and flipping it moves
    // neither location.
    const [shouldChangeDealerToo, setShouldChangeDealerToo] = useState(true)

    // A checked Customer pick shows on the Dealer at once, but both land in
    // calcState together after the Customer's tax-region lookup, so Desk
    // never prices a mixed pair. Display only.
    const [pendingDealerLocation, setPendingDealerLocation] = useState(null)
    // Any committed change (that pick landing, a direct Dealer pick, Reset,
    // which always writes a fresh Customer copy) replaces it.
    useEffect(() => setPendingDealerLocation(null), [calcState.location, calcState.dealer_location])

    return (
        <Box className={'CalcPageInfo_box'}>
            <Stack
                direction="column"
                justifyContent="space-between"
                style={{
                    height: '100%'
                }}
            >
                <div>
                    <Typography
                        variant="h6"
                        component="div"
                        className={'header location-info'}
                    >
                        <LocationOnOutlinedIcon color="primary"/>
                        <span>
                            Location Selection
                        </span>
                    </Typography>

                    <div className={'location-section'}>
                        <div className={'location-section-label'}>
                            Customer
                            <label className={'location-section-toggle'}>
                                <Checkbox
                                    checked={shouldChangeDealerToo}
                                    onChange={(e) => setShouldChangeDealerToo(e.target.checked)}
                                />
                                <span>Change dealer location too</span>
                            </label>
                        </div>
                        <LocationZipField
                            id={'calc-customer-location'}
                            label={'Customer location'}
                            location={calcState.location}
                            onCommit={(location) => {
                                if (shouldChangeDealerToo) setPendingDealerLocation(location)
                                // Also ends it when the pick is superseded and never lands.
                                saveLocation(location, true, shouldChangeDealerToo).finally(() => {
                                    setPendingDealerLocation((pending) => (pending === location ? null : pending))
                                })
                            }}
                        />
                    </div>
                    <div className={'location-section'}>
                        <div className={'location-section-label'}>Dealer</div>
                        <LocationZipField
                            id={'calc-dealer-location'}
                            label={'Dealer location'}
                            location={pendingDealerLocation || calcState.dealer_location}
                            onCommit={(location) => saveLocation(location, false, true)}
                        />
                    </div>
                </div>

                {/* Same bottom action row as Pricing's Lease / Finance / Cash. */}
                <Stack direction="row" alignItems="center">
                    <Button
                        variant="contained"
                        disableElevation
                        className={'btn-edit'}
                        onClick={() => {
                            openModal({
                                element: LocationModal,
                                id: "LocationModal",
                                props: {
                                    customerLocation: calcState.location,
                                    dealerLocation: calcState.dealer_location,
                                    initialShouldChangeDealerToo: shouldChangeDealerToo,
                                    selectLocation: saveLocation,
                                }
                            })
                        }}
                    >
                        Edit
                    </Button>
                </Stack>
            </Stack>
        </Box>
    )
}
